import React, { useRef, useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { chatRoomQueries, removeRoomMember } from '../../../../api/chat'
import { RoomHeader } from '../../../../generated/api/chat-room'
import { MemberRow } from '../../../../generated/api/chat-room-settings'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useAuth } from '../../../../hooks/useAuth'
import { useModal } from '../../../../hooks/useModal'
import { theme } from '../../../../theme/theme'
import { Button, ButtonVariant } from '../../../common/Button'
import { ConfirmationModal } from '../../../common/ConfirmationModal'
import { IconChoices } from '../../../common/Icon'
import { InfiniteScroll } from '../../../common/InfiniteScroll'
import { Modal } from '../../../common/Modal'
import { ModalBody } from '../../../common/Modal/ModalBody'
import { ModalHeader } from '../../../common/Modal/ModalHeader'
import { UserListItem } from '../../../common/UserListItem/UserListItem'
import { ChipAvatar, chipFullName } from '../ChipAvatar'
import { AddMembersModal } from './AddMembersModal'
import { useRoomHeaderMutation } from './useRoomHeaderMutation'

interface RoomMembersModalProps {
  show: boolean
  close: () => void
  header: RoomHeader
}

export const RoomMembersModal = ({
  show,
  close,
  header,
}: RoomMembersModalProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { userId: viewerId } = useAuth()
  const modalRef = useRef<HTMLDivElement | null>(null)
  const [memberToRemove, setMemberToRemove] = useState<MemberRow>()
  const {
    show: showAddMembers,
    open: openAddMembers,
    close: closeAddMembers,
  } = useModal()
  const { canManageMembers } = header.capabilities

  const membersQuery = useInfiniteQuery({
    ...chatRoomQueries.members(header.id),
    enabled: show,
  })
  const members = membersQuery.data?.pages.flatMap((page) => page.items) ?? []

  const { mutate: remove } = useRoomHeaderMutation(
    (userId: string) => removeRoomMember(header.id, userId),
    {
      successMessage: translate(
        'chatSettings.flashMessage.deleteChatRoomMemberSuccess'
      ),
      errorMessage: translate(
        'chatSettings.flashMessage.deleteChatRoomMemberFailure'
      ),
    }
  )

  const subtitleFor = (member: MemberRow): string =>
    [
      member.unitLabel,
      member.isCreator ? translate('chatSettings.form.chatCreator') : undefined,
    ]
      .filter((part): part is string => !!part)
      .join(' · ')

  const iconFor = (member: MemberRow): IconChoices | undefined => {
    if (member.isCreator) return IconChoices.ADMIN
    return canManageMembers && member.userId !== viewerId
      ? IconChoices.DELETE
      : undefined
  }

  return (
    <>
      <Modal ref={modalRef} show={show}>
        {{
          header: (
            <ModalHeader onClose={close}>
              <div className="flex items-center gap-4">
                <p style={theme.textVariants.h3}>
                  {translate('chatSettings.form.title')} ({header.memberCount})
                </p>
                {canManageMembers && (
                  <Button
                    type="button"
                    size="sm"
                    variant={ButtonVariant.PRIMARY}
                    icon={IconChoices.PERSON_ADD}
                    label={translate('common.actions.add')}
                    onClick={openAddMembers}
                    data-testid="room-members-add-button"
                  />
                )}
              </div>
            </ModalHeader>
          ),
          body: (
            <ModalBody className="max-h-96">
              <InfiniteScroll
                onEndReached={() =>
                  membersQuery.fetchNextPage({ cancelRefetch: false })
                }
                isFetching={
                  membersQuery.isLoading || membersQuery.isFetchingNextPage
                }
                rootRef={modalRef}
                className="flex flex-col gap-4"
              >
                {members.map((member) => (
                  <div
                    key={member.userId}
                    data-testid={`room-member-${member.userId}`}
                  >
                    <UserListItem
                      avatar={<ChipAvatar chip={member} />}
                      name={chipFullName(member)}
                      text={subtitleFor(member)}
                      textStyle={theme.textVariants.baseBold}
                      icon={iconFor(member)}
                      disabled={member.isCreator}
                      onIconClick={() => setMemberToRemove(member)}
                      iconTestId={`room-member-${member.userId}-remove`}
                    />
                  </div>
                ))}
              </InfiniteScroll>
            </ModalBody>
          ),
        }}
      </Modal>
      <ConfirmationModal
        title={translate('chatSettings.alert.chatRoomMemberDeleteTitle')}
        description={translate('chatSettings.alert.chatRoomMemberDeleteText')}
        show={memberToRemove !== undefined}
        close={() => setMemberToRemove(undefined)}
        onConfirm={() => memberToRemove && remove(memberToRemove.userId)}
        confirmationButtonLabel={translate('common.actions.remove')}
      />
      {canManageMembers && (
        <AddMembersModal
          show={showAddMembers}
          close={closeAddMembers}
          roomId={header.id}
        />
      )}
    </>
  )
}
